import { useReducer, useCallback } from 'react';
import { ScheduledActivityScheduleType } from 'types/schema-generated-types';

export type UseKeySetting = [KeySettingState, KeySettingDispatch];

export interface KeySettingDispatch {
  onSelectionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDateChange: (date: Date | null, idx: number) => void;
  onDeleteDate: (idx: number) => () => void;
  onAddDate: () => void;
  onToggleAssessOnLogin: () => void;
  refreshState: (state: Partial<KeySettingState>) => void;
}

export interface KeySettingState {
  scheduledDates: Date[];
  scheduleType: string | null;
  assessOnLoginDate: Date | null;
}

type Action =
  | { type: 'DATE_CHANGE'; payload: { date: Date | null; idx: number } }
  | { type: 'DATE_DELETE'; payload: number }
  | { type: 'DATE_ADD' }
  | { type: 'SELECTION_CHANGE'; payload: string }
  | { type: 'ASSESS_ON_LOGIN' }
  | { type: 'REFRESH_STATE'; payload: Partial<KeySettingState> };

const defaultDate = new Date();

const getDefaultState = (state: Partial<KeySettingState>): KeySettingState => ({
  scheduledDates: [],
  assessOnLoginDate: null,
  ...state,
  scheduleType: !state?.scheduleType ? 'NONE' : (state?.scheduleType as string)
});

const keySettingReducer = (state: KeySettingState, action: Action): KeySettingState => {
  switch (action.type) {
    case 'DATE_CHANGE':
      if (!action.payload.date) return state;

      return {
        ...state,
        scheduledDates: state.scheduledDates.map((x: Date, idx: number) => {
          return idx === action.payload.idx ? (action.payload.date as Date) : x;
        })
      };
    case 'DATE_DELETE':
      return {
        ...state,
        scheduledDates: state.scheduledDates.filter((x, index) => action.payload !== index)
      };
    case 'DATE_ADD':
      return {
        ...state,
        scheduledDates: [...state.scheduledDates, defaultDate]
      };
    case 'SELECTION_CHANGE':
      return {
        ...state,
        scheduleType: action.payload,
        scheduledDates:
          action.payload === ScheduledActivityScheduleType.CALENDAR &&
          state.scheduledDates.length === 0
            ? [defaultDate]
            : state.scheduledDates
      };
    case 'ASSESS_ON_LOGIN':
      return {
        ...state,
        assessOnLoginDate: state.assessOnLoginDate ? null : defaultDate
      };
    case 'REFRESH_STATE':
      return getDefaultState(action.payload);
    default:
      return state;
  }
};

const useKeySetting = (initialState: Partial<KeySettingState>): UseKeySetting => {
  const [state, dispatch] = useReducer<React.Reducer<KeySettingState, Action>>(
    keySettingReducer,
    getDefaultState(initialState)
  );

  const onSelectionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch({ type: 'SELECTION_CHANGE', payload: (event.target as HTMLInputElement).value });
  };

  const onToggleAssessOnLogin = (): void => {
    dispatch({ type: 'ASSESS_ON_LOGIN' });
  };

  const refreshState = (state: Partial<KeySettingState> = {}): void => {
    dispatch({ type: 'REFRESH_STATE', payload: state });
  };

  const onDateChange = useCallback((date: Date | null, idx: number): void => {
    dispatch({ type: 'DATE_CHANGE', payload: { date, idx } });
  }, []);

  const onDeleteDate = useCallback(
    (idx: number) => (): void => {
      dispatch({ type: 'DATE_DELETE', payload: idx });
    },
    []
  );

  const onAddDate = useCallback(() => {
    dispatch({ type: 'DATE_ADD' });
  }, []);

  return [
    state,
    {
      onSelectionChange,
      onDateChange,
      onDeleteDate,
      onAddDate,
      refreshState,
      onToggleAssessOnLogin
    }
  ];
};

export default useKeySetting;
